/**
 * DataIntegrityCheckCard Component
 *
 * Scans game data for broken references between objects, templates, types,
 * strings, tags, and folders, and provides auto-fix functionality.
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import IconProvider from '@/components/ui/IconProvider';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import {
  runSanityCheck,
  autoFixBrokenReferences,
  getSanityCheckSummary,
  getDetailedBreakdown,
  sanityCheckStore,
  type SanityCheckReport,
  type SanityCheckProgress,
} from '@/lib/services/SanityCheckService';
import { ChevronDown, ChevronRight } from 'lucide-react';

interface DataIntegrityCheckCardProps {
  gameId: string;
}

export function DataIntegrityCheckCard({ gameId }: DataIntegrityCheckCardProps) {
  const { toast } = useToast();

  const [isScanning, setIsScanning] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [scanProgress, setScanProgress] = useState<SanityCheckProgress | null>(null);
  const [report, setReport] = useState<SanityCheckReport | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [showPreview, setShowPreview] = useState(false);

  const handleRunSanityCheck = async () => {
    setIsScanning(true);
    setScanProgress(null);
    setReport(null);

    try {
      const checkReport = await runSanityCheck(gameId, (progress) => {
        setScanProgress(progress);
      });

      setReport(checkReport);
      sanityCheckStore.setReport(gameId, checkReport);

      if (checkReport.hasBrokenReferences) {
        toast({
          title: 'Issues Found',
          description: `Found ${checkReport.totalCount} broken reference${checkReport.totalCount !== 1 ? 's' : ''}.`,
          variant: 'default',
        });
      } else {
        toast({
          title: 'All Clear',
          description: 'No broken references found. Your data is healthy!',
        });
      }
    } catch (error) {
      console.error('Error running sanity check:', error);
      toast({
        title: 'Error',
        description: 'Failed to run sanity check. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsScanning(false);
      setScanProgress(null);
    }
  };

  const handleFixAllIssues = async () => {
    if (!report) return;

    setIsFixing(true);

    try {
      const result = await autoFixBrokenReferences(report, toast);

      if (result.success) {
        toast({
          title: 'Issues Fixed',
          description: `Successfully fixed ${result.cleanedCount} broken reference${result.cleanedCount !== 1 ? 's' : ''}.`,
        });

        setReport(null);
        sanityCheckStore.clearReport(gameId);
      } else {
        toast({
          title: 'Fix Failed',
          description: `Some issues could not be fixed: ${result.errors.join(', ')}`,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fixing issues:', error);
      toast({
        title: 'Error',
        description: 'Failed to fix issues. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsFixing(false);
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const breakdown = report ? getDetailedBreakdown(report) : [];

  const getChangePreview = () => {
    if (!report) return [];

    const entityChanges = new Map<string, {
      entityType: string;
      entityName: string;
      changes: Array<{
        field: string;
        location: string;
        oldValue: string;
        newValue: string;
        referenceType: string;
      }>;
    }>();

    for (const ref of report.brokenReferences) {
      const key = `${ref.entityType}-${ref.entityId}`;

      if (!entityChanges.has(key)) {
        entityChanges.set(key, {
          entityType: ref.entityType,
          entityName: ref.entityName,
          changes: [],
        });
      }

      const entity = entityChanges.get(key)!;

      let newValue = 'null';
      const lastPathElement = ref.path[ref.path.length - 1];

      if (lastPathElement === 'filterOptions') {
        newValue = 'removed from array';
      } else if (lastPathElement === 'value') {
        newValue = 'null or removed from array';
      } else if (lastPathElement === 'string_id') {
        newValue = 'undefined';
      } else if (lastPathElement === 'blueprint_id') {
        newValue = 'null';
      }

      entity.changes.push({
        field: lastPathElement,
        location: ref.location,
        oldValue: ref.missingId,
        newValue,
        referenceType: ref.referenceType,
      });
    }

    return Array.from(entityChanges.values());
  };

  const changePreview = getChangePreview();

  return (
    <div className="border border-border rounded p-6 space-y-4 bg-card">
      <div className="flex items-start gap-4">
        <IconProvider iconstring="fa-stethoscope" size={12} className="text-primary" />
        <div className="flex-1">
          <h2 className="heading-4">Data Integrity Check</h2>
          <p className="text-muted-foreground mb-4">
            Scan your game data for broken references between objects, templates, types, strings, tags, and folders.
            Broken references can occur when items are deleted or data is imported from external sources.
          </p>

          {!report && !isScanning && (
            <Button onClick={handleRunSanityCheck} size="lg" className="gap-2">
              <IconProvider iconstring="fa-search" size={16} />
              Run Sanity Check
            </Button>
          )}

          {isScanning && (
            <Alert className="mb-4">
              <IconProvider iconstring="fa-spinner" size={14} className="mr-2 animate-spin" />
              <AlertDescription>
                <div className="space-y-2">
                  <div className="font-medium">{scanProgress?.message || 'Scanning...'}</div>
                  {scanProgress && (
                    <div className="w-full bg-muted rounded-full h-2">
                      <div
                        className="bg-primary h-2 rounded-full transition-all"
                        style={{ width: `${scanProgress.progress}%` }}
                      />
                    </div>
                  )}
                </div>
              </AlertDescription>
            </Alert>
          )}

          {report && !isScanning && (
            <div className="space-y-4">
              {report.hasBrokenReferences ? (
                <>
                  <Alert className="border-amber-200 bg-amber-50 dark:bg-amber-950/20">
                    <IconProvider iconstring="fa-exclamation-triangle" size={14} className="mr-2 text-amber-600" />
                    <AlertDescription>
                      <div className="font-medium text-amber-900 dark:text-amber-100 mb-2">
                        {getSanityCheckSummary(report)}
                      </div>
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        These broken references will be cleaned up (set to null) when you click "Fix All Issues".
                      </p>
                    </AlertDescription>
                  </Alert>

                  <div className="space-y-2">
                    {breakdown.map((category) => {
                      const isExpanded = expandedCategories.has(category.category);

                      return (
                        <div key={category.category} className="border rounded">
                          <button
                            onClick={() => toggleCategory(category.category)}
                            className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                              <span className="font-medium">{category.category}</span>
                              <span className="text-sm text-muted-foreground">({category.count})</span>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="border-t p-3 space-y-2 max-h-64 overflow-y-auto">
                              {category.items.map((item, index) => (
                                <div key={index} className="text-sm p-2 rounded bg-muted/30">
                                  <div className="font-medium">{item.entityName}</div>
                                  <div className="text-muted-foreground text-xs mt-1">
                                    {item.location}
                                  </div>
                                  <div className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                                    Missing ID: {item.missingId.substring(0, 8)}...
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Preview Changes Section */}
                  <div className="border rounded">
                    <button
                      onClick={() => setShowPreview(!showPreview)}
                      className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        {showPreview ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                        <IconProvider iconstring="fa-eye" size={14} className="text-blue-600" />
                        <span className="font-medium">Preview Changes</span>
                        <span className="text-sm text-muted-foreground">
                          ({changePreview.length} {changePreview.length === 1 ? 'entity' : 'entities'} will be modified)
                        </span>
                      </div>
                    </button>

                    {showPreview && (
                      <div className="border-t p-4 space-y-4 max-h-96 overflow-y-auto bg-muted/20">
                        {changePreview.map((entity, entityIdx) => (
                          <div key={entityIdx} className="border rounded bg-card">
                            <div className="p-3 border-b bg-muted/50">
                              <div className="font-medium">{entity.entityName}</div>
                              <div className="text-xs text-muted-foreground">
                                {entity.entityType} • {entity.changes.length} change{entity.changes.length !== 1 ? 's' : ''}
                              </div>
                            </div>
                            <div className="p-3 space-y-3">
                              {entity.changes.map((change, changeIdx) => (
                                <div key={changeIdx} className="space-y-1 text-sm">
                                  <div className="text-xs text-muted-foreground">{change.location}</div>
                                  <div className="flex items-start gap-2 p-2 rounded bg-muted/30">
                                    <div className="flex-1 space-y-1">
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs font-mono text-red-600 dark:text-red-400 line-through">
                                          {change.oldValue.substring(0, 20)}{change.oldValue.length > 20 ? '...' : ''}
                                        </span>
                                        <IconProvider iconstring="fa-arrow-right" size={10} className="text-muted-foreground" />
                                        <span className="text-xs font-mono text-green-600 dark:text-green-400">
                                          {change.newValue}
                                        </span>
                                      </div>
                                      <div className="text-xs text-muted-foreground">
                                        Missing {change.referenceType} will be cleared from {change.field}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={handleFixAllIssues}
                      disabled={isFixing}
                      size="lg"
                      variant="default"
                      className="gap-2"
                    >
                      {isFixing ? (
                        <>
                          <IconProvider iconstring="fa-spinner" className="animate-spin" />
                          Fixing Issues...
                        </>
                      ) : (
                        <>
                          <IconProvider iconstring="fa-wrench" size={16} />
                          Fix All Issues
                        </>
                      )}
                    </Button>
                    <Button
                      onClick={handleRunSanityCheck}
                      disabled={isFixing}
                      size="lg"
                      variant="outline"
                      className="gap-2"
                    >
                      <IconProvider iconstring="fa-refresh" size={16} />
                      Run Again
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20">
                    <IconProvider iconstring="fa-check-circle" size={14} className="mr-2 text-green-600" />
                    <AlertDescription>
                      <div className="font-medium text-green-900 dark:text-green-100">
                        No issues found!
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        All references in your game data are valid. Your data integrity is healthy.
                      </p>
                    </AlertDescription>
                  </Alert>

                  <Button
                    onClick={handleRunSanityCheck}
                    size="lg"
                    variant="outline"
                    className="gap-2"
                  >
                    <IconProvider iconstring="fa-refresh" size={16} />
                    Run Again
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DataIntegrityCheckCard;
